import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import {
  dispositivoDaLinhaSchema,
  operadorDaLinhaSchema,
  statusOperacaoSchema,
} from './movimentacoes.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import type { components } from './schema.js';
import { analisar } from './validacao.js';

/**
 * Movimentação em duas etapas (r1-mov, decisões de 2026-09-29).
 *
 * O operador INICIA a movimentação no coletor (produto, origem, quantidade) e
 * depois a ENTREGA, em uma ou mais partes, cada uma com o próprio destino. Cada
 * entrega é uma operação própria e vai ao ERP na hora; a abertura só guarda o
 * saldo. Entrega que passa da quantidade iniciada fica RETIDA até alguém do
 * painel decidir (`Atualizar` ou `Manter`) — divergência nunca se resolve
 * sozinha. O cancelamento pelo painel corta só o saldo pendente.
 *
 * Quantidades vêm como texto na escala do banco (`10.000000`): a tela nunca as
 * passa por `Number` (ver `pages/movimentacoes/quantidade.ts`).
 */

type Esquemas = components['schemas'];

const CAMINHO_COLECAO = montarCaminho('/movimentacoes/aberturas');

export const statusAberturaSchema = z.enum([
  'EmAndamento',
  'ParcialmenteFinalizada',
  'Finalizada',
  'EmDivergencia',
  'Cancelada',
]);

export type StatusAbertura = z.infer<typeof statusAberturaSchema>;

/** Quantas entregas estão em cada ponto do caminho até o ERP. */
const statusNoErpSchema = z.object({
  Registrada: z.number().int().nonnegative(),
  Retida: z.number().int().nonnegative(),
  Enviando: z.number().int().nonnegative(),
  Consolidada: z.number().int().nonnegative(),
  Rejeitada: z.number().int().nonnegative(),
});

export type StatusNoErp = z.infer<typeof statusNoErpSchema>;

/** A entrega que passou da iniciada e espera decisão. */
const entregaRetidaSchema = z.object({
  movimentacaoId: z.string(),
  operacaoId: z.string(),
  /** O que o operador informou. */
  quantidade: z.string(),
  depositoDestino: z.string(),
  filialDestino: z.number().int(),
  registradoEm: z.string(),
});

const camposDaAbertura = {
  id: z.string(),
  sequencia: z.number().int(),
  status: statusAberturaSchema,
  produto: z.string(),
  derivacao: z.string().nullable(),
  depositoOrigem: z.string(),
  filialOrigem: z.number().int(),
  /** Quantidade iniciada. */
  quantidade: z.string(),
  /** Soma de todas as entregas — a retida inclusive. */
  entregue: z.string(),
  /** Zero quando finalizada, em divergência ou cancelada. */
  pendente: z.string(),
  observacao: z.string().nullable(),
  /** Horário do aparelho que iniciou. */
  registradoEm: z.string(),
  dispositivoId: z.string(),
  dispositivo: dispositivoDaLinhaSchema,
  operador: operadorDaLinhaSchema,
  numeroDeEntregas: z.number().int().nonnegative(),
  statusNoErp: statusNoErpSchema,
  /** Quanto a soma passou da iniciada. Zero fora da divergência. */
  excedente: z.string(),
  retida: entregaRetidaSchema.nullable(),
};

/** Conferido contra o OpenAPI em tempo de compilação. */
const aberturaSchema: z.ZodType<Esquemas['AberturaDoPainelResponse']> = z.object(camposDaAbertura);

export type Abertura = z.infer<typeof aberturaSchema>;

const entregaDaAberturaSchema: z.ZodType<Esquemas['EntregaDoDetalheResponse']> = z.object({
  id: z.string(),
  operacaoId: z.string(),
  sequencia: z.number().int(),
  /** Onde a entrega está no caminho até o ERP. */
  status: statusOperacaoSchema,
  /** O que vai (ou foi) ao ERP. */
  quantidade: z.string(),
  /** O que o operador informou, quando `Manter` cortou o excedente. */
  quantidadeInformada: z.string().nullable(),
  depositoDestino: z.string(),
  filialDestino: z.number().int().nullable(),
  observacao: z.string().nullable(),
  registradoEm: z.string(),
  operador: operadorDaLinhaSchema.nullable(),
  dispositivo: dispositivoDaLinhaSchema,
  /** Só o texto do retorno do ERP, já filtrado e cortado pelo middleware. */
  mensagemErp: z.string().nullable(),
});

export type EntregaDaAbertura = z.infer<typeof entregaDaAberturaSchema>;

const detalheDaAberturaSchema: z.ZodType<Esquemas['DetalheDaAberturaResponse']> = z.object({
  ...camposDaAbertura,
  entregas: z.array(entregaDaAberturaSchema),
});

export type DetalheDaAbertura = z.infer<typeof detalheDaAberturaSchema>;

export type DecisaoDaDivergencia = 'Atualizar' | 'Manter';

const divergenciaDecididaSchema: z.ZodType<Esquemas['DivergenciaDecididaResponse']> = z.object({
  id: z.string(),
  decisao: z.enum(['Atualizar', 'Manter']),
  status: statusAberturaSchema,
  quantidade: z.string(),
  entregue: z.string(),
  pendente: z.string(),
  entrega: z.object({
    id: z.string(),
    operacaoId: z.string(),
    status: statusOperacaoSchema,
    quantidade: z.string(),
    quantidadeInformada: z.string().nullable(),
  }),
});

export type DivergenciaDecidida = z.infer<typeof divergenciaDecididaSchema>;

const aberturaCanceladaSchema: z.ZodType<Esquemas['AberturaCanceladaResponse']> = z.object({
  id: z.string(),
  status: statusAberturaSchema,
  quantidade: z.string(),
  entregue: z.string(),
  pendente: z.string(),
  /** O saldo que deixou de existir. */
  pendenteCancelado: z.string(),
});

export type AberturaCancelada = z.infer<typeof aberturaCanceladaSchema>;

const paginaDeAberturasSchema = paginaSchema(aberturaSchema);

export type FiltroDeAberturas = ParametrosDePagina & {
  readonly status?: StatusAbertura;
  /** Código exato. */
  readonly produto?: string;
  /** Acha pela origem da abertura OU pelo destino de alguma entrega. */
  readonly deposito?: string;
  readonly codFil?: number;
  readonly operadorId?: string;
  /** Pelo horário em que a movimentação foi iniciada (ISO). */
  readonly de?: string;
  readonly ate?: string;
};

export async function listarAberturas(filtro: FiltroDeAberturas = {}): Promise<Pagina<Abertura>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      status: filtro.status,
      produto: filtro.produto,
      deposito: filtro.deposito,
      codFil: filtro.codFil,
      operadorId: filtro.operadorId,
      de: filtro.de,
      ate: filtro.ate,
    },
  });

  return analisar(paginaDeAberturasSchema, corpo, CAMINHO_COLECAO);
}

/** O início e cada entrega, em ordem cronológica. */
export async function detalharAbertura(id: string): Promise<DetalheDaAbertura> {
  const caminho = montarCaminho('/movimentacoes/aberturas/{id}', { id });
  return analisar(detalheDaAberturaSchema, await requisitar(caminho), caminho);
}

export type PedidoDeDecisao = {
  readonly decisao: DecisaoDaDivergencia;
  /** Opcional, até 500 caracteres. Em branco não vai. */
  readonly justificativa?: string;
};

/**
 * Decide a divergência (Administrador e Supervisor).
 *
 * Não é idempotente de propósito: a segunda decisão da mesma divergência volta
 * `ABERTURA_SEM_DIVERGENCIA`.
 */
export async function decidirDivergencia(
  id: string,
  pedido: PedidoDeDecisao,
): Promise<DivergenciaDecidida> {
  const caminho = montarCaminho('/movimentacoes/aberturas/{id}/divergencia/decidir', { id });
  const justificativa = pedido.justificativa?.trim() ?? '';
  const corpo = await requisitar(caminho, {
    metodo: 'POST',
    corpo: {
      decisao: pedido.decisao,
      ...(justificativa === '' ? {} : { justificativa }),
    },
  });

  return analisar(divergenciaDecididaSchema, corpo, caminho);
}

/** Cancela só o saldo pendente; as entregas feitas continuam valendo. */
export async function cancelarAbertura(id: string, motivo: string): Promise<AberturaCancelada> {
  const caminho = montarCaminho('/movimentacoes/aberturas/{id}/cancelar', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST', corpo: { motivo: motivo.trim() } });
  return analisar(aberturaCanceladaSchema, corpo, caminho);
}
